import React, { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { rejectPaymentAction } from '@/domain/payments';

import Dialog from '@/components/mui/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import TextFieldBase from '@/components/mui/Form/TextField/TextFieldBase';
import Button from '@mui/material/Button';

interface DialogRejectApprovalProps {
  onClose: () => void;
  isOpen?: boolean;
  id: string;
}

const DialogRejectApproval: React.FC<DialogRejectApprovalProps> = ({ onClose, isOpen = true, id }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const [message, setMessage] = useState('');

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.currentTarget.value);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      new Promise((resolve, reject) => {
        dispatch(rejectPaymentAction({ id, message, resolve, reject }));
      }).finally(() => {
        onClose();
      });
    },
    [dispatch, message, onClose, id],
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={formatMessage({ id: 'payment.modals.rejectApproval.title', defaultMessage: 'Reject Approval?' })}
      maxWidth="sm"
      withActions={false}
      withContent={false}
      okText={null}
      cancelText={null}
      onOk={null}
      okBtnProps={null}
    >
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <TextFieldBase
              value={message}
              onChange={onChange}
              label={formatMessage({
                id: 'payment.modals.rejectApproval.field.details.label',
                defaultMessage: 'Details',
              })}
              placeholder={formatMessage({
                id: 'payment.modals.rejectApproval.field.details.placeholder',
                defaultMessage: 'Details…',
              })}
              fullWidth
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onClose}>
            {formatMessage({
              id: 'button.cancel',
              defaultMessage: 'Cancel',
            })}
          </Button>
          <Button color="error" type="submit" disabled={!message}>
            {formatMessage({
              id: 'payment.footer.actionButton.reject',
              defaultMessage: 'Reject',
            })}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DialogRejectApproval;
